import _ from 'lodash'
import axios from 'axios'
import HttpStatus from 'http-status-codes'
import router from '@/router'
import navigation from '@/utils/navigation'
import EventBus from '@/utils/eventBus'
import { resetStoreWhenLogout } from '@/store/util'
import * as app from '@/api/apps/request'
import store from '@/store'
import { defaultConfig as globalSysHttpCfg } from '@/api/global/consts'
import authentication from '../../authentication'
import { CustomHttp } from './CustomHttp'
import { PipeHttp } from './PipeHttp'
import { axiosDefaults, baseURL } from './consts'
import { syncSessionIdFromResponse } from './session'

export const XSRF = 'X-XSRF-Token'
export const USER_DOMAIN_HEADER = 'X-USER-DOMAIN'
export const SESSION_ID_HEADER = 'x-hkld-session-id'
export const HEADER_REGION = 'region'
export const xsrfCookieName = 'xsrftoken'
export const globalXSRFCookieName = 'pmsr-global-csrf-token'

export function getDocumentCookie () {
  const cookies = document.cookie.split(';').reduce((cookieDict, pairString) => {
    const cookiePair = pairString.split('=')
    const key = (cookiePair[0] || '').trim()
    const val = (cookiePair[1] || '').trim()
    if (key && val) {
      cookieDict[key] = val
    }
    return cookieDict
  }, {})
  return cookies
}

export async function setCsrfToken () {
  const cookies = getDocumentCookie()
  if (!cookies[xsrfCookieName]) {
    const { version } = await app.getApiVer() || 'N/A'
    return version
  }
}

const noRedirectUrls = [`${baseURL}/status`]

export function create (options = {}) {
  const settings = Object.assign({}, axiosDefaults, options)
  const http = axios.create(settings)

  http.interceptors.request.use((config) => {
    const sessionId = sessionStorage.getItem('id')
    if (sessionId) {
      config.headers[SESSION_ID_HEADER] = sessionId
    }
    return config
  })
  http.interceptors.response.use((response) => {
    syncSessionIdFromResponse(response)
    return response
  })

  http.interceptors.response.use(response => response, (error) => {
    if (error.response && error.response.status === HttpStatus.UNAUTHORIZED) {
      EventBus.emit('apps_stop_check_expire')
      EventBus.emit('modules_confirm_continue_close')
      EventBus.emit('modules_no_perm_modal_close')
      $('body>div.reveal-overlay:visible').css('display', 'none')
      resetStoreWhenLogout()
      const redirectUrl = noRedirectUrls.includes(error.config.url) ? null : router.currentRoute.fullPath
      navigation.toLogin(redirectUrl)
    }
    return Promise.reject(error)
  })

  http.interceptors.request.use(
    async config => {
      const isAdMock = store.getters['settings/isAdMock']
      const cookies = getDocumentCookie()
      if (cookies[xsrfCookieName]) {
        config.headers[XSRF] = cookies[xsrfCookieName]
      }
      if (cookies[globalXSRFCookieName] && config.baseURL === globalSysHttpCfg.baseURL) {
        config.headers[XSRF] = cookies[globalXSRFCookieName]
      }

      const region = store.getters['regions/currentRegion']
      const paramRegion = location.pathname === '/adLogin' ? new URLSearchParams(location.search).get('region') : null
      if (paramRegion || !_.isEmpty(region.name)) {
        const currentRegion = paramRegion || region.name
        config.headers[HEADER_REGION] = currentRegion
        if (config.baseURL !== globalSysHttpCfg.baseURL) {
          config.url = `/${currentRegion.toLowerCase()}${config.url}`
        }
      } else {
        console.warn('The currentRegion of VUEX is missing', region)
      }

      const needToken = config.headers.needToken
      const isCrossRegionLogin = store.getters['regions/isCrossRegionLogin']
      if (needToken) {
        let token
        if (
          isAdMock ||
          // Must use the converted token
          ['/api/auth/region/login'].includes(config.url) ||
          // Use the stored token for cross-region login
          isCrossRegionLogin
        ) {
          token = store.getters['settings/jwt']
        } else {
          token = await authentication.acquireToken()
        }
        config.headers.Authorization = `Bearer ` + token
      }

      return config
    },
    error => {
      return Promise.reject(error)
    },
  )

  const methods = {
    silentGet: new CustomHttp(http).get,
    get: new CustomHttp(http).silent(false).get,
    silentPut: new CustomHttp(http).put,
    put: new CustomHttp(http).silent(false).put,
    silentPost: new CustomHttp(http).post,
    post: new CustomHttp(http).silent(false).post,
    head: new CustomHttp(http).head,
    patch: new CustomHttp(http).silent(false).patch,
    delete: new CustomHttp(http).silent(false).delete,
  }
  const newMethods = Object.assign(
    {},
    methods,
    Object.keys(new CustomHttp())
      .reduce((o, key) => {
        const pipeHttp = new PipeHttp()
        if (!Object.keys(pipeHttp).includes(key)) {
          o[key] = (...args) => new CustomHttp(http)[key](...args)
        }
        return o
      }, { pipe: (...args) => new CustomHttp(http).pipe(...args) }),
  )
  return newMethods
}

// TODO @Michelle <Michelle_Zhang@epam.com> add interceptor to handle response rather than using 'response.data.data' in every API call
export default create()
